import React, {useState, useRef, useEffect} from 'react'
import {
    Box,
    Button,
    VStack,
    Image,
    Portal
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {AnimatePresence, motion} from 'framer-motion'
import {getUrl} from '../../../utils/cloudinary-utils'
import usePdTagManager from '../../../hooks/use-pd-tag-manager'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'

const CustomDropdown = ({
    text = 'Select',
    links = [],
    fontFamily,
    buttonTextColor = 'black',
    buttonTextColorDesktop = 'black',
    buttonBgColor = 'white',
    variant = 'default',
    typeId,
    name,
    titlePosition = '',
    imageUrl = '',
    videoUrl = ''
}) => {
    const appOrigin = useAppOrigin()
    const pdTagManager = usePdTagManager()
    const [isOpen, setIsOpen] = useState(false)
    const [position, setPosition] = useState(null)

    const buttonRef = useRef(null)
    const dropdownRef = useRef(null)

    const styleVariant = {
        default: {
            minHeight: '40px',
            padding: '7px 10px',
            fontSize: '16px'
        },
        medium: {
            minHeight: '56px',
            padding: '15px 10px',
            fontSize: '17px',
            display: 'flex',
            gap: '10px'
        }
    }
    // medium variant has white bg and black text for dropdown
    const dropdownBgColor = variant === 'medium' ? 'white' : buttonBgColor
    const dropdownTextColor = {
        base: variant === 'medium' ? 'black.100' : buttonTextColor,
        lg: variant === 'medium' ? 'black.100' : buttonTextColorDesktop
    }

    const iconVariant = {
        default: {
            light: {
                plus: 'UIImages/images/plus-icon.svg',
                minus: 'UIImages/images/minus-icon.svg'
            },
            dark: {
                plus: 'UIImages/images/plus-icon-light.svg',
                minus: 'UIImages/images/minus-icon-light.svg'
            }
        },
        medium: {
            light: {
                plus: 'UIImages/images/plus-icon-medium.svg',
                minus: 'UIImages/images/minus-icon-medium.svg'
            },
            dark: {
                plus: 'UIImages/images/plus-icon-light.svg',
                minus: 'UIImages/images/minus-icon-light.svg'
            }
        }
    }

    const typeColor = buttonTextColor === 'white' ? 'dark' : 'light'

    const PlusIcon = () => (
        <Image
            src={getUrl(iconVariant[variant][typeColor].plus)}
            width="18px"
            height="18px"
            display={isOpen ? 'none' : 'block'}
            alt="plus icon"
        />
    )

    const MinusIcon = () => (
        <Image
            src={getUrl(iconVariant[variant][typeColor].minus)}
            width="18px"
            height="18px"
            display={!isOpen ? 'none' : 'block'}
            alt="minus icon"
        />
    )

    /* -------------------- position calculation -------------------- */
    const updatePosition = () => {
        if (!buttonRef.current) return
        const rect = buttonRef.current.getBoundingClientRect()
        const scrollTop = window.scrollY || document.documentElement.scrollTop
        const scrollLeft = window.scrollX || document.documentElement.scrollLeft

        setPosition({
            top: rect.bottom + scrollTop,
            left: rect.left + scrollLeft,
            width: rect.width
        })
    }

    useEffect(() => {
        if (isOpen) updatePosition()
    }, [isOpen])

    /* -------------------- outside click -------------------- */
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !buttonRef.current.contains(event.target)
            ) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    return (
        <Box fontFamily={fontFamily} position="relative">
            <Button
                ref={buttonRef}
                background={buttonBgColor ? buttonBgColor + '!important' : 'white'}
                color={{base: buttonTextColor, lg: buttonTextColorDesktop}}
                height="auto"
                whiteSpace="normal"
                borderRadius="0"
                border="none"
                onClick={() => setIsOpen(!isOpen)}
                rightIcon={
                    <>
                        <MinusIcon />
                        <PlusIcon />
                    </>
                }
                _hover={{bg: 'white'}}
                _active={{bg: 'white'}}
                _focus={{boxShadow: 'none'}}
                {...styleVariant[variant]}
            >
                {text}
            </Button>

            {/* Dropdown outside Button */}
            <AnimatePresence>
                {isOpen && position && (
                    <Portal>
                        <motion.div
                            ref={dropdownRef}
                            initial={{opacity: 0, y: -10}}
                            animate={{opacity: isOpen ? 1 : 0, y: 0}}
                            exit={{opacity: 0, y: -10}}
                            transition={{duration: 0.15}}
                            style={{
                                position: 'absolute',
                                zIndex: 1,
                                top: position.top,
                                left: position.left,
                                width: position.width
                            }}
                        >
                            <VStack
                                mt="4px"
                                background={dropdownBgColor}
                                borderRadius="0"
                                align="stretch"
                                spacing={0}
                                maxH="143px"
                                minW="170px"
                                overflowY="auto"
                                border={variant === 'medium' ? '1px' : 'none'}
                                sx={{
                                    '&::-webkit-scrollbar': {
                                        width: '6px'
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#999',
                                        borderRadius: '4px'
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: 'transparent'
                                    }
                                }}
                            >
                                {links.map((link, idx) => (
                                    <Box
                                        key={idx}
                                        as="a"
                                        href={link.url}
                                        onClick={(e) => {
                                            // If hash exists and the ID exists as well, we should do smooth scroll and should cover the header height as well
                                            if (e.target?.hash) {
                                                const el = document.querySelector(e.target.hash)
                                                if (el) {
                                                    e.preventDefault()
                                                    const topHeaderHeight =
                                                        document.querySelector('.header-wrapper')
                                                            ?.clientHeight || 80
                                                    const yOffset = -topHeaderHeight
                                                    const y =
                                                        el.getBoundingClientRect().top +
                                                        window.pageYOffset +
                                                        yOffset
                                                    window.scrollTo({top: y, behavior: 'smooth'})
                                                }
                                            }
                                            pdTagManager.selectComponent({
                                                componentId: typeId,
                                                componentName: name,
                                                titleName: link.label,
                                                linkUrl:
                                                    link.url?.indexOf('http') !== -1
                                                        ? link.url
                                                        : `${appOrigin}${link.url}`,
                                                // Optional fields
                                                titlePosition: titlePosition,
                                                imageUrl: imageUrl,
                                                videoUrl: videoUrl
                                            })
                                        }}
                                        padding="12px 16px"
                                        color={dropdownTextColor}
                                        textDecoration="none"
                                        textAlign="left"
                                    >
                                        {link.label}
                                    </Box>
                                ))}
                            </VStack>
                        </motion.div>
                    </Portal>
                )}
            </AnimatePresence>
        </Box>
    )
}

CustomDropdown.propTypes = {
    text: PropTypes.string,
    links: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired
        })
    ),
    fontFamily: PropTypes.string,
    buttonTextColor: PropTypes.string,
    buttonTextColorDesktop: PropTypes.string,
    buttonBgColor: PropTypes.string,
    type: PropTypes.string,
    variant: PropTypes.string,
    typeId: PropTypes.string,
    name: PropTypes.bool,
    titlePosition: PropTypes.any,
    imageUrl: PropTypes.any,
    videoUrl: PropTypes.any
}

export default CustomDropdown
